"use client";

import React from "react";
import { Mail, Phone, MapPin, ArrowRight, Building2, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
export default function ConnectWithUs() {
  return <section id="kết-nối-với-chúng-tôi" className="bg-background py-20 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="flex flex-col gap-10">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-primary font-sans">
                Collaborate for <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-ring">Innovation</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                Partner with NUCETECH to leverage cutting-edge construction technologies and digital transformation solutions. We bridge the gap between academic research and industrial application.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4 group">
                <div className="p-3 bg-primary/5 border border-primary/10 text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary text-lg mb-1">Headquarters</h3>
                  <p className="text-muted-foreground">Hà Nội, Việt Nam</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 bg-primary/5 border border-primary/10 text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Phone className="size-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary text-lg mb-1">Phone</h3>
                  <p className="text-muted-foreground">+84 24 1234 5678</p>
                  <p className="text-sm text-muted-foreground mt-1">Mon-Fri: 8:00 - 17:00</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="p-3 bg-primary/5 border border-primary/10 text-primary shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Mail className="size-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary text-lg mb-1">Email</h3>
                  <p className="text-muted-foreground">contact@nucetech.vn</p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-border">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                Strategic Areas
              </h4>
              <div className="flex flex-wrap gap-3">
                {['Tech Transfer', 'Digital Transformation', 'Smart Construction', 'R&D'].map((tag, i) => <span key={i} data-index={i} className="px-3 py-1 text-xs font-medium border border-border text-primary bg-secondary/10">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute -inset-1 bg-gradient-to-r from-ring/20 to-accent/20 blur-2xl opacity-50 -z-10" />
            
            <Card className="border-border/50 shadow-2xl bg-card/95 backdrop-blur-sm rounded-none overflow-hidden">
              <CardHeader className="bg-primary/5 border-b border-border/50 pb-8 pt-8">
                <CardTitle className="text-2xl font-bold text-primary">Partner With Us</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Fill out the form below to discuss potential collaborations or technology solutions.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 md:p-8">
                <FormProvider formId="connect_form_company_partner" sectionName="connect-with-us" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                organization: "",
                message: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: false
                },
                organization: {
                  required: false
                },
                message: {
                  required: true
                }
              }} submitText="Send Inquiry" successMessage="Thank you for your interest. Our team will contact you shortly." errorMessage="Something went wrong. Please try again later.">
                  <ConnectForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-12 space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="size-16 bg-green-50 text-green-600 flex items-center justify-center rounded-full mb-2">
          <Building2 className="size-8" />
        </div>
        <h3 className="text-xl font-bold text-primary">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-none border-primary/20 hover:bg-primary/5">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Full Name <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input placeholder="Nguyen Van A" {...field} className="rounded-none bg-background border-input focus-visible:ring-ring/30 h-11" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Email Address <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" {...field} className="rounded-none bg-background border-input focus-visible:ring-ring/30 h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+84 9xx xxx xxx" {...field} className="rounded-none bg-background border-input focus-visible:ring-ring/30 h-11" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="organization" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Organization / Company</FormLabel>
            <FormControl>
              <Input placeholder="Company XYZ" {...field} className="rounded-none bg-background border-input focus-visible:ring-ring/30 h-11" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please enter your message"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-medium">Message <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your project or partnership proposal..." {...field} className="rounded-none bg-background border-input focus-visible:ring-ring/30 min-h-[120px] resize-y" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-none h-12 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/10">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Inquiry...
          </> : <>
            Send Message <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}